
import { useState, useEffect, useRef } from 'react';

interface UseTextStreamingOptions {
  text: string;
  enabled: boolean;
  speed?: number; // palavras por minuto
  onComplete?: () => void;
}

export const useTextStreaming = ({ 
  text, 
  enabled, 
  speed = 15000, // ⚡ VELOCIDADE ULTRA-MÁXIMA: 15000 wpm (250 palavras por segundo!)
  onComplete 
}: UseTextStreamingOptions) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (!enabled || !text) {
      setDisplayedText(text);
      setIsComplete(true);
      setIsStreaming(false);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    setIsStreaming(true);

    // ⚡ MODO TOKEN-BY-TOKEN para máxima fluidez
    const words = text.split(' ');
    const wordsPerSecond = speed / 60;
    const intervalMs = Math.max(1, 1000 / wordsPerSecond); // ⚡ MÍNIMO 1ms - velocidade extrema!

    let currentIndex = 0;

    intervalRef.current = setInterval(() => {
      if (currentIndex >= words.length) {
        setIsStreaming(false);
        setIsComplete(true);
        onComplete?.();
        return;
      }

      setDisplayedText(prev => {
        const nextWord = words[currentIndex];
        currentIndex++;
        return prev + (prev ? ' ' : '') + nextWord;
      });
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [text, enabled, speed, onComplete]);

  const skipToEnd = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setDisplayedText(text);
    setIsStreaming(false);
    setIsComplete(true);
    onComplete?.();
  };

  return {
    displayedText,
    isStreaming,
    isComplete,
    skipToEnd
  };
};
